import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import './UpgradePlanModal.css';

const UpgradePlanModal = ({
    show,
    onHide,
    currentPlan,
    targetPlan,
    onConfirm,
    loading = false,
    // Optional Stripe preview payload:
    // { lines: [{ description, amount, quantity, proration }], total_amount_due }
    preview = null,
}) => {
    if (!currentPlan || !targetPlan) return null;

    const priceDifference = targetPlan.price - currentPlan.price;
    const isUpgrade = priceDifference > 0;
    const isDowngrade = priceDifference < 0;

    const hasPreview = !!preview && Array.isArray(preview.lines);
    const totalAmountDue = hasPreview ? Number(preview.total_amount_due || 0) : null;

    const handleConfirm = () => {
        onConfirm(currentPlan, targetPlan);
    };

    return (
        <Modal isOpen={show} toggle={onHide} centered size="md" className="upgrade-plan-modal">
            <ModalHeader toggle={onHide} style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderBottom: 'none',
                borderRadius: '12px 12px 0 0'
            }}>
                <div style={{ color: '#fff', fontWeight: '600', fontSize: '18px' }}>
                    {isUpgrade ? 'Upgrade Plan' : isDowngrade ? 'Downgrade Plan' : 'Change Plan'}
                </div>
            </ModalHeader>

            <ModalBody style={{
                backgroundColor: '#171821',
                color: '#fff',
                padding: '24px'
            }}>
                <div className="upgrade-modal-content">
                    {/* Plan Comparison */}
                    <div className="plan-comparison">
                        <div className="current-plan">
                            <h6 style={{ color: '#a1acb8', marginBottom: '8px' }}>Current Plan</h6>
                            <div className="plan-info">
                                <h5 style={{ color: '#fff', margin: '0 0 4px 0' }}>{currentPlan.title}</h5>
                                <div className="plan-price" style={{ color: '#fff', fontSize: '18px', fontWeight: '600' }}>
                                    ${currentPlan.price}/month
                                </div>
                            </div>
                        </div>

                        <div className="arrow-container" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '20px 0'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: isUpgrade ? 'linear-gradient(135deg, #3a3d4a, #2b2e3b)' :
                                    isDowngrade ? 'linear-gradient(135deg, #ff9800, #f57c00)' :
                                        'linear-gradient(135deg, #2196F3, #1976D2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '18px',
                                fontWeight: 'bold'
                            }}>
                                {isUpgrade ? '↓' : isDowngrade ? '↓' : '→'}
                            </div>
                        </div>

                        <div className="target-plan">
                            <h6 style={{ color: '#a1acb8', marginBottom: '8px' }}>New Plan</h6>
                            <div className="plan-info">
                                <h5 style={{ color: '#fff', margin: '0 0 4px 0' }}>{targetPlan.title}</h5>
                                <div className="plan-price" style={{ color: '#fff', fontSize: '18px', fontWeight: '600' }}>
                                    ${targetPlan.price}/month
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Price Difference / Upgrade Preview */}
                    <div
                        className="price-difference"
                        style={{
                            background: 'rgba(53, 55, 72, 0.6)',
                            borderRadius: '8px',
                            padding: '16px',
                            margin: '20px 0',
                            border: `1px solid ${isUpgrade ? 'rgba(161, 172, 184, 0.3)' :
                                isDowngrade ? 'rgba(255, 152, 0, 0.3)' :
                                    'rgba(33, 150, 243, 0.3)'}`,
                        }}
                    >
                        {hasPreview ? (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 8,
                                    }}
                                >
                                    <span style={{ color: '#a1acb8' }}>You will be charged now</span>
                                    <span
                                        style={{
                                            color: '#fff',
                                            fontSize: '18px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        ${Math.abs(totalAmountDue || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: '#a1acb8',
                                        marginTop: 8,
                                    }}
                                >
                                    {preview.lines.map((line, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                marginBottom: 4,
                                            }}
                                        >
                                            <span>{line.description}</span>
                                            <span
                                                style={{
                                                    color:
                                                        Number(line.amount) < 0
                                                            ? '#4CAF50'
                                                            : '#fff',
                                                }}
                                            >
                                                {Number(line.amount) < 0 ? '-' : '+'}$
                                                {Math.abs(Number(line.amount || 0)).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <span style={{ color: '#a1acb8' }}>
                                    {isUpgrade ? 'Additional cost' : isDowngrade ? 'Savings' : 'Price change'}
                                </span>
                                <span
                                    style={{
                                        color: isUpgrade ? '#fff' : isDowngrade ? '#ff9800' : '#2196F3',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                    }}
                                >
                                    {isUpgrade ? '+' : isDowngrade ? '-' : ''}$
                                    {Math.abs(priceDifference).toFixed(2)}/month
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Benefits Comparison - currently hidden per latest UI requirement */}
                    {false && (
                        <div className="benefits-comparison" style={{ marginTop: '20px' }}>
                            <h6 style={{ color: '#a1acb8', marginBottom: '12px' }}>What's included in {targetPlan.title}:</h6>
                            <div className="benefits-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {targetPlan.benefits && targetPlan.benefits.map((benefit, index) => (
                                    <div key={index} className="benefit-item" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: index < targetPlan.benefits.length - 1 ? '1px solid rgba(53, 55, 72, 0.3)' : 'none'
                                    }}>
                                        <span className="benefit-icon" style={{ marginRight: '12px' }}>
                                            {benefit.state === 'positive' ? (
                                                <svg width="16" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M15.9422 1.06719L5.94219 11.0672C5.82496 11.1845 5.66588 11.2505 5.5 11.2505C5.33412 11.2505 5.17504 11.1845 5.05781 11.0672L0.682812 6.69219C0.438599 6.44797 0.438599 6.05203 0.682812 5.80781C0.927026 5.5636 1.32297 5.5636 1.56719 5.80781L5.5 9.74141L15.0578 0.182813C15.302 -0.0614009 15.698 -0.0614009 15.9422 0.182813C16.1864 0.427026 16.1864 0.822974 15.9422 1.06719Z" fill="#FFFFFF" />
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M13.2071 3.79289C13.5976 4.18342 13.5976 4.81658 13.2071 5.20711L10.4142 8L13.2071 10.7929C13.5976 11.1834 13.5976 11.8166 13.2071 12.2071C12.8166 12.5976 12.1834 12.5976 11.7929 12.2071L9 9.41421L6.20711 12.2071C5.81658 12.5976 5.18342 12.5976 4.79289 12.2071C4.40237 11.8166 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 4.18342 4.79289 3.79289C5.18342 3.40237 5.81658 3.40237 6.20711 3.79289L9 6.58579L11.7929 3.79289C12.1834 3.40237 12.8166 3.40237 13.2071 3.79289Z" fill="#F44336" />
                                                </svg>
                                            )}
                                        </span>
                                        <span className="benefit-text" style={{ color: '#fff', fontSize: '14px' }}>
                                            {benefit.benefit_value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Important Note */}
                    <div className="important-note" style={{
                        background: 'rgba(255, 152, 0, 0.1)',
                        border: '1px solid rgba(255, 152, 0, 0.3)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginTop: '20px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <span style={{ color: '#ff9800', marginRight: '8px', fontSize: '16px' }}>⚠️</span>
                            <div style={{ color: '#a1acb8', fontSize: '13px' }}>
                                <strong style={{ color: '#ff9800' }}>Important:</strong>
                                {isUpgrade
                                    ? ' Your plan change will take effect after your current plan ends. You will be charged a prorated amount for this upgrade.'
                                    : isDowngrade
                                        ? ' Your plan change will take effect immediately. You will receive a prorated credit for the downgrade.'
                                        : ' Your billing will be adjusted accordingly.'}
                            </div>
                        </div>
                    </div>
                </div>
            </ModalBody>

            <ModalFooter style={{
                backgroundColor: '#171821',
                borderTop: '1px solid rgba(53, 55, 72, 0.6)',
                padding: '16px 24px'
            }}>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', width: '100%' }}>
                    <Button
                        color="secondary"
                        onClick={onHide}
                        disabled={loading}
                        style={{ minWidth: '80px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color={isUpgrade ? 'success' : isDowngrade ? 'warning' : 'primary'}
                        className={isUpgrade ? 'btn-upgrade' : ''}
                        onClick={handleConfirm}
                        disabled={loading}
                        style={{ minWidth: '120px' }}
                    >
                        {loading ? 'Processing...' :
                            isUpgrade ? 'Upgrade Plan' :
                                isDowngrade ? 'Downgrade Plan' :
                                    'Change Plan'}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default UpgradePlanModal;
